import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { post } from '../lib/api.js';
import { ErrorNote } from '../components/ui.jsx';
import { Icon } from '../lib/icons.jsx';
import { LOGO } from '../lib/logo.js';
import { checkPassword } from '../lib/password.js';

/**
 * The two halves of forgetting a password, in one page.
 *
 * Without a token it asks for an address; with one it asks for a new password.
 * They are the same screen because they are the same errand, and because a
 * person who lands here from a stale link needs the way to ask for a fresh one
 * in front of them rather than a dead end and a back button.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token');
  return token ? <ChooseNew token={token} /> : <AskForLink />;
}

function Shell({ title, sub, children }) {
  return (
    <div className="login">
      <div className="login-form">
        <div className="login-box">
          <img src={LOGO} alt="SevenMentor" style={{ height: 34, marginBottom: 22 }} />
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontWeight: 750, fontSize: 22, letterSpacing: '-.035em' }}>{title}</div>
            <div className="txt-sm muted">{sub}</div>
          </div>
          <div className="card"><div className="card-b">{children}</div></div>
          <div className="txt-sm muted" style={{ marginTop: 18, textAlign: 'center' }}>
            <Link to="/login">Back to sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ ask for a link */

function AskForLink() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await post('/auth/forgot-password', { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <Shell title="Check your email" sub="If that address is on the roll, a link is on its way">
        <p className="txt-sm" style={{ margin: 0, lineHeight: 1.6 }}>
          We have sent a link to <b>{email.trim()}</b> if it belongs to an employee here.
          It works once and stops working after an hour.
        </p>
        <p className="txt-sm muted" style={{ marginBottom: 0, lineHeight: 1.6 }}>
          Nothing has changed yet — your current password still works until you use the link.
          If it does not arrive, check the spam folder, or ask HR to set one for you.
        </p>
      </Shell>
    );
  }

  return (
    <Shell title="Forgotten password" sub="We will email you a link to choose a new one">
      <form onSubmit={submit}>
        <div className="field">
          <label htmlFor="forgot-email">Work email</label>
          <input
            id="forgot-email" className="inp" type="email" value={email} autoComplete="username"
            autoFocus placeholder="you@sevenmentor.com"
            onChange={(e) => setEmail(e.target.value)} required
          />
          <div className="hint">The address you sign in with.</div>
        </div>
        <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={busy || !email.trim()}>
          {busy ? 'Sending…' : <><Icon name="mail" />Email me a link</>}
        </button>
      </form>
      <ErrorNote error={error} />
    </Shell>
  );
}

/* --------------------------------------------------------- choose a new one */

function ChooseNew({ token }) {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  // The same rules the server enforces, so the reasons appear as somebody
  // types rather than after a round trip that rejects the whole form.
  const check = checkPassword(password);
  const mismatch = again.length > 0 && again !== password;
  const ready = check.ok && !mismatch && again.length > 0;

  useEffect(() => {
    if (!done) return undefined;
    const t = setTimeout(() => navigate('/login'), 4000);
    return () => clearTimeout(t);
  }, [done, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      await post('/auth/reset-password', { token, password });
      setDone(true);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <Shell title="Password changed" sub="You can sign in with it now">
        <p className="txt-sm" style={{ margin: 0, lineHeight: 1.6 }}>
          Every other session on your account has been signed out. Taking you to the
          sign-in page…
        </p>
      </Shell>
    );
  }

  return (
    <Shell title="Choose a new password" sub="This link works once">
      <form onSubmit={submit}>
        <div className="field">
          <label htmlFor="reset-password">New password</label>
          <input
            id="reset-password" className="inp" type="password" value={password}
            autoComplete="new-password" autoFocus
            onChange={(e) => setPassword(e.target.value)} required
          />
          {password.length > 0 && !check.ok && (
            <div className="hint" style={{ color: 'var(--crit)' }}>{check.rules.filter((r) => check.failed.includes(r.id)).map((r) => r.label).join(' · ')}</div>
          )}
        </div>

        <div className="field">
          <label htmlFor="reset-again">Type it again</label>
          <input
            id="reset-again" className="inp" type="password" value={again}
            autoComplete="new-password"
            onChange={(e) => setAgain(e.target.value)} required
          />
          {mismatch && <div className="hint" style={{ color: 'var(--crit)' }}>Those two do not match.</div>}
        </div>

        <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={busy || !ready}>
          {busy ? 'Saving…' : <><Icon name="lock" />Set the password</>}
        </button>
      </form>
      <ErrorNote error={error} />
      <div className="txt-sm muted" style={{ marginTop: 14, lineHeight: 1.55 }}>
        If this says the link has expired, ask for a{' '}
        <Link to="/reset-password">fresh one</Link> — they last an hour and work once.
      </div>
    </Shell>
  );
}
